import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import TopNav from "components/organisms/Navigation";

import Hero from "components/molecules/Hero";
import MobileNav from "components/organisms/Navigation/MobileNav";
import { FaCaretUp, FaCaretDown, FaLevelUpAlt } from "react-icons/fa";
import useNavData from "components/molecules/navData";
import { setSectionInView } from "../../redux/app/app.action";
import Headroom from "react-headroom";

const Dashboard = (props: { setSectionInView: (data: string) => void }) => {
  const { pathname } = useLocation();
  const { sectionIds } = useNavData();
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const [showTopButton, setShowTopButton] = useState(false);
  const [track, setTrack] = useState<{ ids: string[]; index: number }>({ ids: [], index: 0 });
  const orderedRef = useRef<string[]>([]);

  // The nav bar order is not the page order (My Resume sits before Contact in the
  // nav but after it on the page), so sort by real position instead.
  const getOrderedIds = (): string[] => {
    const found = sectionIds
      .map((id) => ({ id, top: document.getElementById(id)?.getBoundingClientRect().top }))
      .filter((section): section is { id: string; top: number } => section.top !== undefined)
      .sort((a, b) => a.top - b.top)
      .map((section) => section.id);

    if (found.length) orderedRef.current = found;
    return found.length ? found : orderedRef.current;
  };

  // The section the reader is on: the last one whose top has passed the 40% line.
  // Measured from the DOM so it is never stale after a manual scroll or a nav tap.
  const getActiveIndex = (ids: string[]): number => {
    const line = window.innerHeight * 0.4;
    let index = 0;

    ids.forEach((id, i) => {
      const top = document.getElementById(id)?.getBoundingClientRect().top;
      if (top !== undefined && top <= line) index = i;
    });

    return index;
  };

  useEffect(() => {
    let frame = 0;

    const handleFloatingNav = () => {
      setShowFloatingNav(window.scrollY >= window.innerHeight);
      setShowTopButton(window.scrollY >= window.innerHeight);
    };

    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const ids = getOrderedIds();
        const index = getActiveIndex(ids);
        setTrack((prev) => (prev.index === index && prev.ids.length === ids.length ? prev : { ids, index }));
      });
    };

    handleFloatingNav();
    sync();

    window.addEventListener("scroll", handleFloatingNav, { passive: true });
    window.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleFloatingNav);
      window.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Each view is a separate page, so land at the top with a clean active section.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    setShowFloatingNav(false);
    setShowTopButton(false);
    props.setSectionInView("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Always resolved against the section on screen, not a remembered counter.
  const goTo = (offset: number) => {
    const ids = getOrderedIds();
    if (!ids.length) return;

    const target = ids[getActiveIndex(ids) + offset];
    if (target) document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
  };

  const nextId = track.ids[track.index + 1];
  const prevId = track.ids[track.index - 1];

  return (
    <div
      className="min-h-screen w-full relative z-[22] flex flex-col justify-between"
      id="Home"
    >
      <div id="top"></div>
      <div className="relative w-full h-full">
        {/* Navigation */}

        {showFloatingNav && (
          <Headroom
            pin
            style={{ zIndex: 40 }}
            wrapperStyle={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 40 }}
          >
            <TopNav
              wrapperStyle="backdrop-blur-md !bg-BackDrop_d_sm hover:!bg-BackDrop_d_xl mobile:hidden fixed z-[25] top-5 left-0 right-0 mx-auto rounded-full py-5 !px-8 bg-white animate-fade_in transition ease-in-out duration-500 max-w-6xl w-full"
            />
            <MobileNav
              wrapperStyle="mobile:!flex fixed z-[25] top-0 w-full bg-DarkBg10 py-5"
            />
          </Headroom>
        )}

        {/* Hero Section */}
        <Hero />

        {/* Main Content */}
        <main className="w-full h-fit flex flex-col">
          <Outlet />
        </main>

        {/* Floating controls: back to top, then step to the section before / after the one on screen */}
        <div className="fixed z-20 bottom-16 mobile:bottom-5 right-5 flex flex-col items-end gap-4">
          <button
            type="button"
            onClick={() => document.getElementById("Home")?.scrollIntoView({ behavior: "smooth" })}
            aria-label="Back to top"
            className={`${showTopButton ? "flex" : "hidden"} backdrop-blur !cursor-pointer group fixed sm:relative z-20 bottom-[200px] sm:bottom-auto right-5 sm:right-auto border border-Primary mobile:border-none bg-NoColor mobile:bg-Primary_Accents_md hover:bg-Primary rounded-full w-12 h-12 mobile:w-11 mobile:h-11 items-center justify-center`}
          >
            <FaLevelUpAlt className="w-1/2 h-1/2 text-Primary mobile:text-white group-hover:text-white group-hover:animate-pulse" />

            <span className="absolute right-12 px-4 py-1 rounded-full hidden group-hover:block animate-slide_left2 bg-Primary text-white font-normal text-sm whitespace-nowrap transition ease-in-out !duration-250">
              Back to top
            </span>
          </button>

          <button
            type="button"
            onClick={() => goTo(-1)}
            disabled={!prevId}
            aria-label={prevId ? `Go to previous section, ${prevId}` : "No previous section"}
            className="backdrop-blur mobile:flex !cursor-pointer group fixed sm:relative z-20 bottom-[120px] sm:bottom-auto right-5 sm:right-auto border border-Primary mobile:border-none bg-NoColor mobile:bg-Primary_Accents_md hover:bg-Primary rounded-full w-12 h-12 mobile:w-11 mobile:h-11 flex justify-center items-center disabled:!hidden"
          >
            <FaCaretUp className="w-3/5 h-3/5 text-Primary mobile:text-white group-hover:text-white group-hover:animate-pulse" />

            <span className="absolute right-12 px-4 py-1 rounded-full hidden group-hover:block animate-slide_left2 bg-Primary text-white font-normal text-sm whitespace-nowrap transition ease-in-out !duration-250">
              {prevId}
            </span>
          </button>

          <button
            type="button"
            onClick={() => goTo(1)}
            disabled={!nextId}
            aria-label={nextId ? `Go to next section, ${nextId}` : "No next section"}
            className="backdrop-blur !cursor-pointer group fixed sm:relative z-20 bottom-16 sm:bottom-auto right-5 sm:right-auto border border-Primary mobile:border-none bg-NoColor mobile:bg-Primary_Accents_md hover:bg-Primary rounded-full w-12 h-12 mobile:w-11 mobile:h-11 flex justify-center items-center disabled:!hidden"
          >
            <FaCaretDown className="w-3/5 h-3/5 text-Primary mobile:text-white group-hover:text-white group-hover:animate-pulse" />

            <span className="absolute right-12 px-4 py-1 rounded-full hidden group-hover:block animate-slide_left2 bg-Primary text-white font-normal text-sm whitespace-nowrap transition ease-in-out !duration-250">
              {nextId}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  setSectionInView: (data: string) => dispatch(setSectionInView(data)),
});

export default connect(null, mapDispatchToProps)(Dashboard);
